/* Site chrome: glassy nav and footer.
   Ported from shared.jsx; navigation wired to React Router. */
import { CSSProperties, MouseEvent as ReactMouseEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Logo, Btn, NeuralCanvas, useIsMobile } from './ui';
import { LineIcon } from './icons';
import { COOKIE_SETTINGS_EVENT } from './consent';
import { useGo, navHandler, View, VIEW_PATHS } from './nav';

const NAV: [View, string][] = [
  ['home', 'Home'],
  ['portfolio', 'Products'],
  ['services', 'Services'],
  ['about', 'About'],
  ['contact', 'Contact'],
];

const viewFromPath = (pathname: string): View => {
  const match = (Object.keys(VIEW_PATHS) as View[]).find(
    (key) =>
      VIEW_PATHS[key] === pathname || (key !== 'home' && pathname.startsWith(VIEW_PATHS[key])),
  );
  // Product pages (e.g. /table-finch) live under the Products nav item.
  if (match === 'tablefinch' || match === 'nyasa' || match === 'compasso') return 'portfolio';
  return match ?? 'home';
};

export const NavBar = () => {
  const go = useGo();
  const { pathname } = useLocation();
  const view = viewFromPath(pathname);
  const isMobile = useIsMobile();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 14);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const goTo = (k: View) => (e: ReactMouseEvent) => {
    setMenuOpen(false);
    navHandler(go, k)(e);
  };
  const linkStyle = (k: View): CSSProperties => ({
    fontFamily: 'var(--font-body)',
    fontWeight: 600,
    fontSize: 14.5,
    textDecoration: 'none',
    color: view === k ? 'var(--indigo)' : 'var(--slate)',
    background: view === k ? 'var(--paper-cool)' : 'transparent',
    border: 'none',
    padding: '9px 16px',
    borderRadius: 'var(--r-pill)',
    cursor: 'pointer',
    transition: 'all .2s',
  });
  return (
    <header
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, transition: 'all .3s' }}
    >
      <div
        style={{
          margin: scrolled ? '12px auto' : '0 auto',
          maxWidth: scrolled ? 1180 : '100%',
          width: scrolled ? 'calc(100% - 32px)' : '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: scrolled ? '12px 22px' : '20px 40px',
          borderRadius: scrolled ? 'var(--r-pill)' : 0,
          background: scrolled ? 'rgba(255,255,255,.72)' : 'rgba(255,255,255,.55)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: scrolled ? '1px solid rgba(255,255,255,.7)' : '1px solid rgba(231,232,239,.7)',
          boxShadow: scrolled ? '0 14px 40px -16px rgba(24,0,89,.22)' : 'none',
          transition: 'all .35s cubic-bezier(.2,.7,.2,1)',
        }}
      >
        <a
          href={VIEW_PATHS.home}
          onClick={goTo('home')}
          aria-label='Femtoware home'
          style={{ display: 'inline-flex', textDecoration: 'none', cursor: 'pointer' }}
        >
          <Logo />
        </a>
        {isMobile ? (
          <button
            type='button'
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              gap: 5,
              width: 46,
              height: 46,
              padding: '0 12px',
              borderRadius: 13,
              border: '1px solid',
              borderColor: menuOpen ? 'rgba(0,160,247,.55)' : 'var(--line)',
              background: menuOpen ? 'rgba(255,255,255,.9)' : 'rgba(255,255,255,.6)',
              boxShadow: menuOpen ? '0 0 0 4px rgba(0,160,247,.12)' : 'none',
              cursor: 'pointer',
              transition: 'box-shadow .28s, background .28s, border-color .28s',
            }}
          >
            {/* Stepped, two-tone bars echo the layered double-F logo (green/blue/indigo),
                then converge into a clean X when the menu opens. */}
            {[
              { w: 22, c: '#09AA00', open: 'translateY(7.5px) rotate(45deg)' },
              { w: 15, c: '#00A0F7', open: 'scaleX(0)' },
              { w: 19, c: '#180059', open: 'translateY(-7.5px) rotate(-45deg)' },
            ].map((bar, i) => (
              <span
                key={bar.c}
                style={{
                  display: 'block',
                  height: 2.5,
                  borderRadius: 2,
                  width: menuOpen ? 22 : bar.w,
                  background: bar.c,
                  transformOrigin: 'center',
                  transition:
                    'transform .32s cubic-bezier(.2,.7,.2,1), width .32s cubic-bezier(.2,.7,.2,1), opacity .2s',
                  transform: menuOpen ? bar.open : 'none',
                  opacity: menuOpen && i === 1 ? 0 : 1,
                }}
              />
            ))}
          </button>
        ) : (
          <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {NAV.map(([k, l]) => (
              <a
                key={k}
                href={VIEW_PATHS[k]}
                onClick={goTo(k)}
                aria-current={view === k ? 'page' : undefined}
                style={linkStyle(k)}
                onMouseEnter={(e) => {
                  if (view !== k) e.currentTarget.style.color = 'var(--indigo)';
                }}
                onMouseLeave={(e) => {
                  if (view !== k) e.currentTarget.style.color = 'var(--slate)';
                }}
              >
                {l}
              </a>
            ))}
            <div style={{ marginLeft: 10 }}>
              <Btn small href={VIEW_PATHS.contact} onClick={goTo('contact')}>
                Start a project →
              </Btn>
            </div>
          </nav>
        )}
      </div>
      {isMobile && menuOpen && (
        <nav
          style={{
            margin: '8px 16px 0',
            padding: 12,
            borderRadius: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            background: 'rgba(255,255,255,.9)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(231,232,239,.9)',
            boxShadow: '0 20px 50px -18px rgba(24,0,89,.28)',
            animation: 'fwPop .22s cubic-bezier(.2,.9,.2,1)',
          }}
        >
          {NAV.map(([k, l]) => (
            <a
              key={k}
              href={VIEW_PATHS[k]}
              onClick={goTo(k)}
              aria-current={view === k ? 'page' : undefined}
              style={{ ...linkStyle(k), fontSize: 16, padding: '13px 16px' }}
            >
              {l}
            </a>
          ))}
          <div style={{ marginTop: 6 }}>
            <Btn href={VIEW_PATHS.contact} onClick={goTo('contact')}>
              Start a project →
            </Btn>
          </div>
        </nav>
      )}
    </header>
  );
};

const FOOTER_COLS: [string, string[]][] = [
  [
    'Services',
    [
      'Software built with NYASA',
      'AI agents for operations',
      'Optimisation & data',
      'Trust & security',
    ],
  ],
  ['Products', ['NYASA', 'Compasso', 'Table Finch', 'All products']],
  ['Company', ['About Us', 'Contact', 'Support']],
];

const footerTarget = (item: string): View | null => {
  if (item === 'Table Finch') return 'tablefinch';
  if (item === 'NYASA') return 'nyasa';
  if (item === 'Compasso') return 'compasso';
  if (item === 'All products') return 'portfolio';
  if (item === 'About Us') return 'about';
  if (item === 'Contact' || item === 'Support') return 'contact';
  if (/Software|operations|data|Trust/.test(item)) return 'services';
  return null;
};

export const Footer = () => {
  const go = useGo();
  return (
    <footer
      style={{ background: 'var(--void)', color: '#fff', position: 'relative', overflow: 'hidden' }}
    >
      <div style={{ position: 'absolute', inset: 0, opacity: 0.5 }}>
        <NeuralCanvas density={30} opacity={0.6} />
      </div>
      <div
        style={{
          position: 'absolute',
          width: 500,
          height: 500,
          left: -120,
          top: -200,
          borderRadius: '50%',
          background: 'radial-gradient(circle,rgba(0,160,247,.22),transparent 65%)',
        }}
      />
      <div
        style={{
          position: 'relative',
          maxWidth: 1180,
          margin: '0 auto',
          padding: '72px 40px 36px',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr', gap: 40 }}>
          <div>
            <Logo dark />
            <p
              style={{
                color: 'rgba(255,255,255,.6)',
                fontSize: 14.5,
                lineHeight: 1.6,
                margin: '18px 0 0',
                maxWidth: 280,
              }}
            >
              Femtoware Infotech LLP — building AI software that does real work. Faster. Smarter.
              Precise.
            </p>
            {/* Social links return once the company LinkedIn page is live. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 22,
                fontSize: 14,
                fontWeight: 600,
                color: 'rgba(255,255,255,.75)',
              }}
            >
              <LineIcon name='pin' size={16} stroke='currentColor' />
              Bangalore · Berlin
            </div>
          </div>
          {FOOTER_COLS.map(([heading, items]) => (
            <div key={heading}>
              <div
                className='fw-display'
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,.45)',
                  marginBottom: 16,
                }}
              >
                {heading}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                {items.map((i) => {
                  const target = footerTarget(i);
                  const linkStyle: CSSProperties = {
                    fontSize: 14.5,
                    color: 'rgba(255,255,255,.7)',
                    cursor: 'pointer',
                    transition: 'color .2s',
                    width: 'fit-content',
                    textDecoration: 'none',
                  };
                  if (target) {
                    return (
                      <a
                        key={i}
                        href={VIEW_PATHS[target]}
                        onClick={navHandler(go, target)}
                        style={linkStyle}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = 'rgba(255,255,255,.7)';
                        }}
                      >
                        {i}
                      </a>
                    );
                  }
                  return (
                    <span
                      key={i}
                      style={linkStyle}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'rgba(255,255,255,.7)';
                      }}
                    >
                      {i}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div
          style={{
            marginTop: 54,
            paddingTop: 26,
            borderTop: '1px solid rgba(255,255,255,.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 14,
          }}
        >
          <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,.5)' }}>
            © 2026 Femtoware Infotech LLP · GSTIN 32AAIFF1675Q1Z0
          </span>
          <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,.5)' }}>
            <a
              href={VIEW_PATHS.privacy}
              onClick={navHandler(go, 'privacy')}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Privacy
            </a>{' '}
            ·{' '}
            <button
              type='button'
              onClick={() => window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT))}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              Cookie settings
            </button>{' '}
            ·{' '}
            <a
              href='mailto:contactus@fwinfotech.com'
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              contactus@fwinfotech.com
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};
